import { Check } from 'lucide-react'
import { DropdownMenu as Menu } from 'radix-ui'
import { type ComponentProps, cloneElement, type ReactElement, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { trackMenu } from './focus-return'
import { floatingPanel, menuItem } from './select'

export const DropdownMenu = Menu.Root
export const DropdownMenuTrigger = Menu.Trigger
export const DropdownMenuGroup = Menu.Group
export const DropdownMenuRadioGroup = Menu.RadioGroup

export function DropdownMenuContent({
  className,
  align = 'end',
  sideOffset = 4,
  ...props
}: ComponentProps<typeof Menu.Content>) {
  return (
    <Menu.Portal>
      <Menu.Content
        // Remember the menu's trigger while it is open: a dialog opened from one of its items
        // returns focus there.
        ref={trackMenu}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(floatingPanel, 'min-w-44 p-1', className)}
        {...props}
      />
    </Menu.Portal>
  )
}

export interface DropdownMenuItemProps extends ComponentProps<typeof Menu.Item> {
  icon?: ReactNode
  tone?: 'default' | 'danger'
  shortcut?: string
}

export function DropdownMenuItem({
  className,
  icon,
  tone = 'default',
  shortcut,
  children,
  ...props
}: DropdownMenuItemProps) {
  return (
    <Menu.Item
      className={cn(menuItem, tone === 'danger' && 'text-ink-red [&_svg]:text-ink-red', className)}
      {...props}
    >
      {icon ? <span className="flex text-ink-muted">{icon}</span> : null}
      <span className="flex-1 truncate">{children}</span>
      {shortcut ? (
        <kbd dir="ltr" className="font-mono text-2xs text-ink-muted">
          {shortcut}
        </kbd>
      ) : null}
    </Menu.Item>
  )
}

export interface DropdownMenuLinkItemProps extends Omit<ComponentProps<typeof Menu.Item>, 'asChild'> {
  /** The element to render as the item (a router Link, an anchor); its children are replaced. */
  link: ReactElement<{ className?: string; children?: ReactNode }>
  icon?: ReactNode
  tone?: 'default' | 'danger'
}

/** A menu item that navigates: the link element carries the item's styling and keyboard behaviour. */
export function DropdownMenuLinkItem({
  link,
  icon,
  tone = 'default',
  className,
  children,
  ...props
}: DropdownMenuLinkItemProps) {
  return (
    <Menu.Item asChild {...props}>
      {cloneElement(link, {
        className: cn(
          menuItem,
          tone === 'danger' && 'text-ink-red [&_svg]:text-ink-red',
          link.props.className,
          className,
        ),
        children: (
          <>
            {icon ? <span className="flex text-ink-muted">{icon}</span> : null}
            <span className="flex-1 truncate">{children}</span>
          </>
        ),
      })}
    </Menu.Item>
  )
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.CheckboxItem>) {
  return (
    <Menu.CheckboxItem className={cn(menuItem, 'ps-8', className)} {...props}>
      <Menu.ItemIndicator className="absolute start-2 flex text-stamp">
        <Check aria-hidden />
      </Menu.ItemIndicator>
      {children}
    </Menu.CheckboxItem>
  )
}

export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem className={cn(menuItem, 'ps-8', className)} {...props}>
      <Menu.ItemIndicator className="absolute start-2 flex text-stamp">
        <Check aria-hidden />
      </Menu.ItemIndicator>
      {children}
    </Menu.RadioItem>
  )
}

export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof Menu.Label>) {
  return (
    <Menu.Label className={cn('px-2 pt-1.5 pb-1 text-xs font-medium text-ink-muted', className)} {...props} />
  )
}

export function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof Menu.Separator>) {
  return <Menu.Separator className={cn('-mx-1 my-1 h-px bg-rule', className)} {...props} />
}
